import AcknowledgementNotice from 'components/AcknowledgementNotice/AcknowledgementNotice';
import Modal from 'tailwind-components/Modal/Modal';
import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { Info } from 'lucide-react';
import BadgeWithIcon from 'tailwind-components/BadgeWithIcon/BadgeWithIcon';
import Button from 'tailwind-components/Button/Button';
import AppointmentDetailsRequestBody from 'tailwind-components/AppointmentDetails/AppointmentDetailsRequestBody';
import useAppointmentActions from 'views/MyAppointments/AppointmentDetails/useAppointmentActions';
import { Box } from '@mui/material';
import { isNewAppointmentRequestType } from 'helpers/appointments/appointments';
import { cn } from 'lib/utils';

function CloseRequestAcknowledgementSection({
  action,
  closeRequestSelectValue,
  setCloseRequestSelectValue,
  selectedCloseReasonOption,
  isLoading
}) {
  if (
    action?.value !== 'close_request' ||
    !Array.isArray(action?.selectOptions) ||
    !action.selectOptions.length
  ) {
    return null;
  }

  return (
    <div className='w-full min-w-0'>
      <label
        className='text-sm font-medium text-[var(--color-grey)]'
        htmlFor='close-request-reason-details'
      >
        {action.selectLabel}
      </label>
      <select
        id='close-request-reason-details'
        className='mt-1.5 box-border min-h-11 w-full rounded-md border border-[var(--color-grey-lighter)] bg-[var(--color-input-background)] px-3 py-2 text-sm text-[var(--color-black)] outline-none focus:border-[var(--color-primary-emphasis)] focus:ring-1 focus:ring-[var(--color-primary-emphasis)] disabled:opacity-60'
        value={closeRequestSelectValue}
        onChange={e => setCloseRequestSelectValue(e.target.value)}
        disabled={isLoading}
      >
        <option value=''>Select an option</option>
        {action.selectOptions.map(opt => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {!closeRequestSelectValue && (
        <p className='text-sm text-semantic-danger'>
          Please select a reason for closing the request.
        </p>
      )}

      {selectedCloseReasonOption && (
        <BadgeWithIcon
          className={cn(
            'mt-4 border',
            selectedCloseReasonOption.helperBgColor,
            selectedCloseReasonOption.helperBorderColor
          )}
          icon={
            <div
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full',
                selectedCloseReasonOption.iconBgColor
              )}
              aria-hidden
            >
              <Info className='size-4 text-white' strokeWidth={2} />
            </div>
          }
        >
          <p
            className={cn(
              'text-sm font-semibold leading-snug',
              selectedCloseReasonOption.helperTitleColor
            )}
          >
            {selectedCloseReasonOption.helperTitle}
          </p>
          <div className='flex flex-col gap-1'>
            <p
              className={cn(
                'text-sm leading-relaxed',
                selectedCloseReasonOption.helperDescriptionColor
              )}
            >
              {selectedCloseReasonOption.helperDescription}
            </p>
            <p
              className={cn(
                'text-sm leading-relaxed',
                selectedCloseReasonOption.helperDescriptionColor
              )}
            >
              {selectedCloseReasonOption.helperFooter}
            </p>
          </div>
        </BadgeWithIcon>
      )}
    </div>
  );
}

CloseRequestAcknowledgementSection.propTypes = {
  action: propTypes.object,
  closeRequestSelectValue: propTypes.string.isRequired,
  setCloseRequestSelectValue: propTypes.func.isRequired,
  selectedCloseReasonOption: propTypes.object,
  isLoading: propTypes.bool.isRequired
};

export default function AppointmentDetails({
  appointmentDetails,
  setAppointmentDetails,
  carrierConsidersStates,
  newAppointmentsVideoURL
}) {
  const {
    isLoading,
    credentials,
    errors,
    action,
    isAcknowledgementOpen,
    isAcknowledgementAccepted,
    actionOptions,
    setAction,
    setIsAcknowledgementOpen,
    setIsAcknowledgementAccepted,
    closeRequestSelectValue,
    setCloseRequestSelectValue,
    handleCredentialChange,
    handleSubmit,
    resetAcknowledgement
  } = useAppointmentActions(appointmentDetails, carrierConsidersStates);

  const showNewAppointmentVideo =
    !!newAppointmentsVideoURL &&
    isNewAppointmentRequestType(appointmentDetails.request_type);

  const hasNote = !!appointmentDetails.custom_note_for_agent;
  const hasInstructionsSection = hasNote || showNewAppointmentVideo;

  const [isInstructionsExpanded, setIsInstructionsExpanded] = useState(false);

  const instructionsToggleLabel = (() => {
    if (!hasNote && showNewAppointmentVideo) {
      return isInstructionsExpanded ? 'Hide video' : 'View carrier video';
    }
    return isInstructionsExpanded ? 'Hide instructions' : 'View instructions';
  })();

  const selectedCloseReasonOption = useMemo(() => {
    if (action?.value !== 'close_request' || !action?.selectOptions?.length) {
      return null;
    }
    return (
      action.selectOptions.find(o => o.value === closeRequestSelectValue) ||
      null
    );
  }, [action, closeRequestSelectValue]);

  return (
    <>
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      <Modal
        active={!isAcknowledgementOpen}
        size='large'
        title={
          isLoading ? 'Updating Request...' : 'Appointment Request Details'
        }
        closeModal={isLoading ? () => {} : () => setAppointmentDetails(null)}
        showScrollbar
      >
        <AppointmentDetailsRequestBody
          appointmentDetails={appointmentDetails}
          setAppointmentDetails={setAppointmentDetails}
          isLoading={isLoading}
          credentials={credentials}
          errors={errors}
          action={action}
          actionOptions={actionOptions}
          setAction={setAction}
          handleCredentialChange={handleCredentialChange}
          handleSubmit={handleSubmit}
          hasInstructionsSection={hasInstructionsSection}
          hasNote={hasNote}
          showNewAppointmentVideo={showNewAppointmentVideo}
          newAppointmentsVideoURL={newAppointmentsVideoURL}
          isInstructionsExpanded={isInstructionsExpanded}
          setIsInstructionsExpanded={setIsInstructionsExpanded}
          instructionsToggleLabel={instructionsToggleLabel}
          onOpenAcknowledgement={() => setIsAcknowledgementOpen(true)}
        />
      </Modal>

      <Modal
        active={isAcknowledgementOpen}
        title={isLoading ? 'Sending Request...' : 'Acknowledgement'}
        size='large'
        closeModal={resetAcknowledgement}
        showScrollbar
      >
        <Box
          display='flex'
          flexDirection='column'
          justifyContent='space-between'
          height='100%'
          width='100%'
        >
          <Box
            flexGrow={1}
            display='flex'
            flexDirection='column'
            alignItems='stretch'
            marginTop='20px'
            gap={2}
            width='100%'
            px={1}
          >
            <AcknowledgementNotice
              message={action?.acknowledgement}
              isError={!isAcknowledgementAccepted}
              checked={isAcknowledgementAccepted}
              onChange={() =>
                setIsAcknowledgementAccepted(!isAcknowledgementAccepted)
              }
              variant='info'
              disabled={isLoading}
              addBottomMargin={false}
            />

            <CloseRequestAcknowledgementSection
              action={action}
              closeRequestSelectValue={closeRequestSelectValue}
              setCloseRequestSelectValue={setCloseRequestSelectValue}
              selectedCloseReasonOption={selectedCloseReasonOption}
              isLoading={isLoading}
            />
          </Box>

          <Box
            display='flex'
            justifyContent='space-between'
            width='100%'
            mt={2}
            gap='10px'
          >
            <Button
              disabled={isLoading}
              onClick={resetAcknowledgement}
              fill
              text='Cancel'
              variant='danger'
              size='medium'
            />
            <Button
              disabled={
                isLoading ||
                !isAcknowledgementAccepted ||
                (action?.value === 'close_request' && !closeRequestSelectValue)
              }
              isLoading={isLoading}
              variant='primaryLight'
              text='Submit'
              fill
              onClick={e => handleSubmit(e, () => setAppointmentDetails(null))}
              size='medium'
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
}

AppointmentDetails.propTypes = {
  appointmentDetails: propTypes.object.isRequired,
  setAppointmentDetails: propTypes.func.isRequired,
  carrierConsidersStates: propTypes.bool.isRequired,
  newAppointmentsVideoURL: propTypes.string
};
